import React, { useState, useEffect } from 'react';

const MySponsorshipsTable = ({ currentUserId }) => {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);

  useEffect(() => {
    fetchSponsorships();
  }, []);

  const fetchSponsorships = async () => {
    try {
      const response = await fetch('/api/v1/sponsorships/my_sponsorships', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        setSponsorships(data.data?.initiated_sponsorships || []);
      } else {
        setError(data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (sponsorshipId) => {
    setResendingId(sponsorshipId);
    setFlashMessage(null);
    try {
      const response = await fetch(`/api/v1/sponsorships/${sponsorshipId}/resend_invitation`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        setFlashMessage({ type: 'success', text: data.message || 'Invitation renvoyée !' });
      } else {
        setFlashMessage({ type: 'danger', text: data.message || 'Erreur lors de l\'envoi' });
      }
    } catch (err) {
      setFlashMessage({ type: 'danger', text: 'Erreur de connexion' });
    } finally {
      setResendingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: '#fff3cd', color: '#856404', label: 'En attente' },
      approved: { bg: '#d4edda', color: '#155724', label: 'Approuvée' },
      completed: { bg: '#cce5ff', color: '#004085', label: 'Complétée' },
      rejected: { bg: '#f8d7da', color: '#721c24', label: 'Rejetée' }
    };
    const s = config[status] || { bg: '#e2e3e5', color: '#383d41', label: status };
    return (
      <span style={{
        background: s.bg, color: s.color, padding: '4px 12px',
        borderRadius: 999, fontSize: '0.78rem', fontWeight: 700
      }}>
        {s.label}
      </span>
    );
  };

  const getRegistrationBadge = (sponsorship) => {
    if (sponsorship.filleul_registered) {
      return (
        <span style={{
          background: '#d4edda', color: '#155724', padding: '4px 12px',
          borderRadius: 999, fontSize: '0.78rem', fontWeight: 700
        }}>
          ✅ Inscrit
        </span>
      );
    }
    if (sponsorship.status === 'approved' || sponsorship.status === 'completed') {
      return (
        <span style={{
          background: '#fff3cd', color: '#856404', padding: '4px 12px',
          borderRadius: 999, fontSize: '0.78rem', fontWeight: 700
        }}>
          ⏳ En attente d'inscription
        </span>
      );
    }
    return (
      <span style={{
        background: '#e2e3e5', color: '#383d41', padding: '4px 12px',
        borderRadius: 999, fontSize: '0.78rem', fontWeight: 700
      }}>
        — Non applicable
      </span>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      invite: 'Invité',
      adherent_sympathisant: 'Sympathisant',
      adherent_participant: 'Participant',
      adherent_actif: 'Actif',
      membre_fondateur: 'Fondateur'
    };
    return labels[level] || level;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#7c2837' }}>
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p style={{ color: '#7a4050', marginTop: 10, fontSize: '0.9rem' }}>Chargement de vos cooptations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" style={{ borderRadius: 12 }}>
        {error}
      </div>
    );
  }

  if (sponsorships.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0', color: '#7a4050' }}>
        <p style={{ margin: 0, fontSize: '0.95rem' }}>
          Vous n'avez encore initié aucune demande de cooptation.
        </p>
      </div>
    );
  }

  return (
    <div>
      {flashMessage && (
        <div
          className={`alert alert-${flashMessage.type} alert-dismissible fade show`}
          role="alert"
          style={{ borderRadius: 12, marginBottom: 16 }}
        >
          {flashMessage.text}
          <button type="button" className="btn-close" onClick={() => setFlashMessage(null)}></button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Email du filleul</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Niveau</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Statut cooptation</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Soutiens</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Inscription filleul</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Date</th>
              <th style={{ color: '#7c2837', fontWeight: 700, borderBottom: '2px solid rgba(124,40,55,0.2)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sponsorships.map((s) => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600, color: '#2c0e16' }}>
                  {s.filleul_email}
                </td>
                <td>{getLevelLabel(s.requested_level)}</td>
                <td>{getStatusBadge(s.status)}</td>
                <td>
                  <span style={{ fontWeight: 600 }}>
                    {s.supporters_count || 0}
                  </span>
                  <span style={{ color: '#7a4050' }}> / {s.required_supporters_count}</span>
                  {s.status === 'pending' && (
                    <div className="progress mt-1" style={{ height: 4 }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.min(((s.supporters_count || 0) / s.required_supporters_count) * 100, 100)}%`,
                          backgroundColor: '#7c2837'
                        }}
                      />
                    </div>
                  )}
                </td>
                <td>{getRegistrationBadge(s)}</td>
                <td style={{ color: '#7a4050', whiteSpace: 'nowrap' }}>
                  {formatDate(s.created_at)}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                    <a
                      href={`/sponsorships/${s.id}`}
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(124,40,55,0.08)',
                        color: '#7c2837',
                        border: '1px solid rgba(124,40,55,0.15)',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: '0.78rem'
                      }}
                    >
                      Détails
                    </a>
                    {(s.status === 'approved' || s.status === 'completed') && !s.filleul_registered && (
                      <button
                        className="btn btn-sm"
                        disabled={resendingId === s.id}
                        onClick={() => handleResendInvitation(s.id)}
                        style={{
                          background: '#7c2837',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          opacity: resendingId === s.id ? 0.7 : 1
                        }}
                      >
                        {resendingId === s.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                            Envoi...
                          </>
                        ) : (
                          '📧 Renvoyer invitation'
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MySponsorshipsTable;
